/**
 * Vendor info and company members. Tenant `manage_vendors` adds members by email and removes them.
 */

import Avatar from "@cloudscape-design/chat-components/avatar"
import Alert from "@cloudscape-design/components/alert"
import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import ColumnLayout from "@cloudscape-design/components/column-layout"
import Container from "@cloudscape-design/components/container"
import ContentLayout from "@cloudscape-design/components/content-layout"
import FormField from "@cloudscape-design/components/form-field"
import Header from "@cloudscape-design/components/header"
import Input from "@cloudscape-design/components/input"
import Modal from "@cloudscape-design/components/modal"
import Spinner from "@cloudscape-design/components/spinner"
import Table, { type TableProps } from "@cloudscape-design/components/table"
import { useNavigate, useParams } from "react-router-dom"
import { useNotifications } from "@/contexts/NotificationContext"
import { useMyAccess } from "@/hooks/queries/useMyAccess"
import { isForbidden, parseApiErrorMessage } from "@/utils/apiErrors"
import Breadcrumb from "../../components/Navigation/Breadcrumb"
import Pagination from "../../components/Pagination/Pagination"
import {
  useAddVendorUser,
  useRemoveVendorUser,
  useVendorDetail,
  useVendorUsers,
} from "../../hooks/queries/useVendors"
import { usePageParam } from "../../hooks/usePageParam"
import { type VendorUser } from "../../services/vendorRegistryApi"
import { getAvatarColor, getInitials } from "../../utils/avatar"

import SpaceBetween from "@cloudscape-design/components/space-between"
import StatusIndicator from "@cloudscape-design/components/status-indicator"
import { type CSSProperties, useState } from "react"
import { isValidEmail } from "@/utils/email"

const USERS_PER_PAGE = 5
const PAGE_STYLE: CSSProperties = { minHeight: "calc(100vh - 60px)" }

function userColumns(
  onRemove: ((user: VendorUser) => void) | null,
): TableProps.ColumnDefinition<VendorUser>[] {
  const columns: TableProps.ColumnDefinition<VendorUser>[] = [
    {
      id: "user",
      header: "User",
      cell: (user) => (
        <SpaceBetween direction="horizontal" size="s" alignItems="center">
          <Avatar
            ariaLabel={user.email}
            initials={getInitials(user.email.replace(/[.@+]/g, " "))}
            width={32}
            style={{ root: { background: getAvatarColor(user.email), color: "white" } }}
          />
          <span>{user.email}</span>
        </SpaceBetween>
      ),
    },
    {
      id: "status",
      header: "Status",
      cell: (u) =>
        u.status === "active" ? (
          <StatusIndicator type="success">Active</StatusIndicator>
        ) : (
          <StatusIndicator type="pending">Pending</StatusIndicator>
        ),
    },
  ]
  if (onRemove) {
    columns.push({
      id: "actions",
      header: "Actions",
      cell: (u) => (
        <Button variant="inline-link" ariaLabel="Remove" onClick={() => onRemove(u)}>
          Remove
        </Button>
      ),
    })
  }
  return columns
}

/**
 * Add a person to the company by email. A new email becomes a pending member; a member of another
 * company moves here.
 */
function AddUserModal({
  vendorId,
  vendorName,
  onClose,
}: {
  vendorId: string
  vendorName: string
  onClose: () => void
}) {
  const { showToast } = useNotifications()
  const addUser = useAddVendorUser(vendorId)
  const [email, setEmail] = useState("")
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async () => {
    const trimmed = email.trim()
    if (!isValidEmail(trimmed)) {
      setError("Enter a valid email address")
      return
    }
    setError(null)
    try {
      await addUser.mutateAsync(trimmed)
      showToast({
        subject: "User added",
        message: `${trimmed} now belongs to ${vendorName}`,
        variant: "success",
      })
      onClose()
    } catch (err) {
      setError(parseApiErrorMessage(err, "Failed to add user"))
    }
  }

  return (
    <Modal
      visible
      onDismiss={onClose}
      header={`Add user - ${vendorName}`}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={addUser.isPending}
              onClick={() => void handleSubmit()}
            >
              Add user
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <FormField
        label="Email"
        description="Someone who has not signed in yet is added as pending. A member of another vendor moves to this one."
        errorText={error ?? undefined}
      >
        <Input
          value={email}
          onChange={({ detail }) => setEmail(detail.value)}
          type="email"
          ariaLabel="Email"
          placeholder="name@vendor.com"
        />
      </FormField>
    </Modal>
  )
}

/**
 * Confirm removing a person from the company. Their grants stay.
 */
function RemoveUserModal({
  user,
  vendorId,
  vendorName,
  onClose,
}: {
  user: VendorUser
  vendorId: string
  vendorName: string
  onClose: () => void
}) {
  const { showToast } = useNotifications()
  const remove = useRemoveVendorUser(vendorId)
  const [error, setError] = useState<string | null>(null)

  const handleRemove = async () => {
    try {
      await remove.mutateAsync(user.principal_id)
      showToast({
        subject: "User removed",
        message: `${user.email} no longer belongs to ${vendorName}`,
        variant: "success",
      })
      onClose()
    } catch (err) {
      setError(parseApiErrorMessage(err, "Failed to remove user"))
    }
  }

  return (
    <Modal
      visible
      onDismiss={onClose}
      header="Remove user"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={remove.isPending}
              onClick={() => void handleRemove()}
            >
              Remove
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Box>
          Remove {user.email} from {vendorName}? They keep any access already granted to them.
        </Box>
        {error && <Alert type="error">{error}</Alert>}
      </SpaceBetween>
    </Modal>
  )
}

const VendorDetail = () => {
  const { vendorId } = useParams<{ vendorId: string }>()
  const navigate = useNavigate()
  const [usersPage, setUsersPage] = usePageParam("usersPage")

  const isAdmin = useMyAccess().holdsTenantCapability("manage_vendors")
  const [adding, setAdding] = useState(false)
  const [removing, setRemoving] = useState<VendorUser | null>(null)

  const { data: vendor, isLoading: loading, error } = useVendorDetail(vendorId)
  const {
    data: vendorUsers = [],
    isLoading: usersLoading,
    error: usersError,
  } = useVendorUsers(vendorId)

  const usersPageCount = Math.max(1, Math.ceil(vendorUsers.length / USERS_PER_PAGE))
  const currentPageUsers = vendorUsers.slice(
    (usersPage - 1) * USERS_PER_PAGE,
    usersPage * USERS_PER_PAGE,
  )

  if (loading) {
    return (
      <div style={PAGE_STYLE}>
        <Box textAlign="center" padding="xxl">
          <Spinner size="large" />
          <Box variant="p" color="text-body-secondary">
            Loading vendor...
          </Box>
        </Box>
      </div>
    )
  }

  if (error || !vendor) {
    return (
      <div style={PAGE_STYLE}>
        <Breadcrumb items={[{ label: "All Vendors", path: "/vendors" }, { label: "Not Found" }]} />
        <Alert
          type="error"
          action={<Button onClick={() => navigate("/vendors")}>Back to Vendors</Button>}
        >
          {error instanceof Error ? error.message : "Vendor not found"}
        </Alert>
      </div>
    )
  }

  return (
    <div style={PAGE_STYLE}>
      <Breadcrumb
        items={[{ label: "All Vendors", path: "/vendors" }, { label: vendor.vendor_name }]}
      />

      <ContentLayout
        header={
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <Avatar
              ariaLabel={vendor.vendor_name}
              initials={getInitials(vendor.vendor_name)}
              width={48}
              style={{ root: { background: getAvatarColor(vendor.vendor_id), color: "white" } }}
            />
            <Header variant="h1" description={vendor.vendor_type || undefined}>
              {vendor.vendor_name}
            </Header>
          </div>
        }
      >
        <Box padding={{ top: "l" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
            <Container header={<Header variant="h2">Details</Header>}>
              <ColumnLayout columns={4} variant="text-grid">
                <div>
                  <Box variant="awsui-key-label">Contact Email</Box>
                  <div>{vendor.contact_email || "-"}</div>
                </div>
                <div>
                  <Box variant="awsui-key-label">Contact Name</Box>
                  <div>{vendor.contact_name || "-"}</div>
                </div>
                <div>
                  <Box variant="awsui-key-label">Created</Box>
                  <div>{new Date(vendor.created_at).toLocaleDateString()}</div>
                </div>
                <div>
                  <Box variant="awsui-key-label">Created By</Box>
                  <div>{vendor.created_by}</div>
                </div>
              </ColumnLayout>
              {vendor.notes && (
                <Box padding={{ top: "m" }}>
                  <Box variant="awsui-key-label">Notes</Box>
                  <div>{vendor.notes}</div>
                </Box>
              )}
            </Container>

            <Container
              header={
                <Header
                  variant="h2"
                  counter={usersError ? undefined : `(${vendorUsers.length})`}
                  description="People who belong to this vendor. They are suggested when granting vendor access on its DTAs."
                  actions={
                    isAdmin ? <Button onClick={() => setAdding(true)}>Add user</Button> : undefined
                  }
                >
                  Users
                </Header>
              }
            >
              {usersError ? (
                <Alert type={isForbidden(usersError) ? "info" : "error"}>
                  {isForbidden(usersError)
                    ? "You don't have permission to see this vendor's users."
                    : parseApiErrorMessage(usersError, "Failed to load users")}
                </Alert>
              ) : (
                <Table
                  variant="embedded"
                  columnDefinitions={userColumns(isAdmin ? setRemoving : null)}
                  items={currentPageUsers}
                  loading={usersLoading}
                  loadingText="Loading users..."
                  trackBy="principal_id"
                  empty="No users belong to this vendor yet."
                  pagination={
                    usersPageCount > 1 ? (
                      <Pagination
                        currentPage={usersPage}
                        pageCount={usersPageCount}
                        hasMore={false}
                        onNext={() => setUsersPage((p) => p + 1)}
                        onPrevious={() => setUsersPage((p) => p - 1)}
                        onPageChange={setUsersPage}
                      />
                    ) : undefined
                  }
                />
              )}
            </Container>
          </div>
        </Box>
      </ContentLayout>
      {adding && (
        <AddUserModal
          vendorId={vendor.vendor_id}
          vendorName={vendor.vendor_name}
          onClose={() => setAdding(false)}
        />
      )}
      {removing && (
        <RemoveUserModal
          user={removing}
          vendorId={vendor.vendor_id}
          vendorName={vendor.vendor_name}
          onClose={() => setRemoving(null)}
        />
      )}
    </div>
  )
}

export default VendorDetail
